'use client';

import React from 'react';
import { HighlightCard } from '@/components/cards/HighlightCard';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { useOwner } from '../../context/OwnerProvider';
import { ModalTrigger } from '@/components/ui/ModalTrigger';
import dynamic from 'next/dynamic';
import { cn } from '@/lib/cn';
import { analyticsDataAttrs } from '@/lib/analytics/data-attrs';

// Lazy load modal components
const EvictionsModal = dynamic(() =>
  import('./modals/EvictionsModal').then(mod => mod.EvictionsModal),
);

const LitigationModal = dynamic(() =>
  import('./modals/LitigationModal').then(mod => mod.LitigationModal),
);

export function OwnerHighlights() {
  const { owner } = useOwner();
  const { openDataSummary } = owner;

  if (!openDataSummary) {
    return null;
  }

  const { numEvictions, numLitigations } = openDataSummary;

  // Helper function to determine highlight type based on value
  const getHighlightType = (value: number): 'neutral' | 'caution' | 'positive' => {
    if (value === 0) return 'neutral';
    return 'caution'; // Any non-zero value is concerning for these metrics
  };

  // Helper function to format display value
  const formatValue = (value: number): string => {
    return value === 0 ? '0' : value?.toString();
  };

  return (
    <div>
      {/* Section Header */}
      <SectionHeader title="Highlights" />

      {/* Highlights Grid */}
      <div className={cn('grid grid-cols-2 gap-4', 'px-2', 'md:px-4')}>
        <ModalTrigger label="Evictions" content={<EvictionsModal />} urlKey="evictions">
          <HighlightCard
            type={getHighlightType(numEvictions || 0)}
            valueType="text"
            value={formatValue(numEvictions || 0)}
            label="Evictions"
            {...analyticsDataAttrs('contact_evictions', {
              contactId: owner.id,
              contactSlug: owner.slug,
            })}
          />
        </ModalTrigger>
        <ModalTrigger label="Litigation cases" content={<LitigationModal />} urlKey="litigation">
          <HighlightCard
            type={getHighlightType(numLitigations || 0)}
            valueType="text"
            value={formatValue(numLitigations || 0)}
            label="Litigation cases"
            {...analyticsDataAttrs('contact_litigations', {
              contactId: owner.id,
              contactSlug: owner.slug,
            })}
          />
        </ModalTrigger>
      </div>
    </div>
  );
}
